<script setup lang="ts">
  import {
    Chip,
    ChipSize,
    Input,
    InputSize,
    Slider,
    Typo,
    TypoVariant,
    type SliderModelValue,
  } from '@travelata-libs/ui-kit';
  import { watchDebounced } from '@vueuse/core';
  import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
  import { BUDGET_FILTER_COMMIT_DELAY } from '../config/filters';
  import { SERP_BUDGET_FILTER_TEXTS } from '../config/texts';
  import {
    countBudgetDigits,
    findBudgetCaret,
    formatBudgetAmount,
    formatBudgetTourists,
    fromBudgetSliderValue,
    parseBudgetAmount,
    toBudgetSliderBounds,
    toBudgetSliderValue,
    toggleBudgetPreset,
  } from '../lib/budgetFilter';
  import type { FiltersModel, SerpBudgetFilterGroupView, SerpBudgetPresetView } from '../model/types';
  import { useSerpFilters } from '../model/useSerpFilters';

  interface BudgetFilterProps {
    group: SerpBudgetFilterGroupView;
    titleHidden?: boolean;
  }

  const props = withDefaults(defineProps<BudgetFilterProps>(), { titleHidden: false });

  type BudgetBound = keyof FiltersModel['budget'];

  type BudgetTexts = Record<BudgetBound, string>;

  interface BudgetField {
    bound: BudgetBound;
    label: string;
    placeholder: string;
  }

  interface FocusedField {
    bound: BudgetBound;
    placeholder: string;
  }

  const NBSP = '\u00A0';

  const toBudgetText = (amount: number): string => (amount ? formatBudgetAmount(amount) : '');

  const { filtersModelDraft, setBudget } = useSerpFilters();

  const rootRef = ref<HTMLElement | null>(null);
  const typedTexts = ref<Record<BudgetBound, string | null>>({ min: null, max: null });
  const focusedField = ref<FocusedField | null>(null);

  const budget = computed<FiltersModel['budget']>(() => filtersModelDraft.value.budget);

  const texts = computed<BudgetTexts>(() => ({
    min: typedTexts.value.min ?? toBudgetText(budget.value.min),
    max: typedTexts.value.max ?? toBudgetText(budget.value.max),
  }));

  const touristsLabel = computed<string>(() => formatBudgetTourists(props.group.touristsCount));

  const pricePlaceholders = computed<BudgetTexts>(() => ({
    min: formatBudgetAmount(props.group.minPrice),
    max: formatBudgetAmount(props.group.maxPrice),
  }));

  const placeholderOf = (bound: BudgetBound): string =>
    focusedField.value?.bound === bound ? focusedField.value.placeholder : pricePlaceholders.value[bound];

  const fields = computed<BudgetField[]>(() => [
    { bound: 'min', label: SERP_BUDGET_FILTER_TEXTS.min, placeholder: placeholderOf('min') },
    { bound: 'max', label: SERP_BUDGET_FILTER_TEXTS.max, placeholder: placeholderOf('max') },
  ]);

  const sliderBounds = computed<SliderModelValue>(() => toBudgetSliderBounds(props.group));
  const sliderValue = computed<SliderModelValue>(() => toBudgetSliderValue(budget.value, sliderBounds.value));
  const isSliderDisabled = computed<boolean>(() => sliderBounds.value.min === sliderBounds.value.max);

  const typedAmount = (bound: BudgetBound): number => {
    const text = typedTexts.value[bound];

    return text === null ? budget.value[bound] : parseBudgetAmount(text);
  };

  const commitTypedTexts = (): void => {
    if (typedTexts.value.min === null && typedTexts.value.max === null) return;

    const nextBudget = { min: typedAmount('min'), max: typedAmount('max') };
    typedTexts.value = { min: null, max: null };

    if (nextBudget.min === budget.value.min && nextBudget.max === budget.value.max) return;
    setBudget(nextBudget);
  };

  watchDebounced(typedTexts, commitTypedTexts, { debounce: BUDGET_FILTER_COMMIT_DELAY, deep: true });

  onBeforeUnmount(commitTypedTexts);

  const onSliderChange = (value: SliderModelValue): void => {
    setBudget(fromBudgetSliderValue(value, sliderBounds.value, props.group));
  };

  const fieldName = (bound: BudgetBound): string => `budget-filter-${bound}`;

  const findTextInput = (bound: BudgetBound): HTMLInputElement | null =>
    rootRef.value?.querySelector<HTMLInputElement>(`input[name="${fieldName(bound)}"]`) ?? null;

  const restoreCaret = (bound: BudgetBound, digitsBeforeCaret: number): void => {
    const input = findTextInput(bound);

    if (!input || input !== document.activeElement) return;

    const caret = findBudgetCaret(input.value, digitsBeforeCaret);
    input.setSelectionRange(caret, caret);
  };

  const onTextInput = async (bound: BudgetBound, value: string): Promise<void> => {
    const caret = findTextInput(bound)?.selectionStart ?? value.length;
    const digitsBeforeCaret = countBudgetDigits(value.slice(0, caret));

    typedTexts.value[bound] = value;
    await nextTick();
    typedTexts.value[bound] = toBudgetText(parseBudgetAmount(value));
    await nextTick();
    restoreCaret(bound, digitsBeforeCaret);
  };

  const onFieldFocus = (bound: BudgetBound): void => {
    focusedField.value = { bound, placeholder: pricePlaceholders.value[bound] };
  };

  const onFieldBlur = (): void => {
    focusedField.value = null;
    commitTypedTexts();
  };

  const presetLabel = (preset: number): string =>
    `${SERP_BUDGET_FILTER_TEXTS.upTo} ${formatBudgetAmount(preset)}${NBSP}${SERP_BUDGET_FILTER_TEXTS.currency}`;

  // Chip прокидывает клик на корневой элемент даже задизейбленным
  const onPresetClick = (preset: SerpBudgetPresetView): void => {
    if (preset.disabled) return;

    setBudget(toggleBudgetPreset(budget.value, preset.value));
  };
</script>

<template>
  <div
    ref="rootRef"
    class="fr-budget-filter"
    data-testid="budget-filter"
  >
    <div
      v-if="!titleHidden || touristsLabel"
      class="fr-budget-filter__header"
    >
      <Typo
        v-if="!titleHidden"
        class="fr-budget-filter__title"
        :variant="TypoVariant.BodyLRegular"
        tag="span"
        data-testid="budget-filter-title"
      >
        {{ group.title }}
      </Typo>
      <Typo
        v-if="touristsLabel"
        class="fr-budget-filter__tourists"
        :variant="TypoVariant.BodyLRegular"
        tag="span"
        data-testid="budget-filter-tourists"
      >
        {{ touristsLabel }}
      </Typo>
    </div>

    <div class="fr-budget-filter__fields">
      <div
        v-for="field in fields"
        :key="field.bound"
        class="fr-budget-filter__field"
      >
        <Typo
          class="fr-budget-filter__field-label"
          :variant="TypoVariant.BodyMRegular"
          tag="span"
        >
          {{ field.label }}
        </Typo>
        <!-- ui-kit Input пересчитывает ширину поля при смене значения, но не placeholder -->
        <Input
          :key="`${field.bound}-${field.placeholder}`"
          :model-value="texts[field.bound]"
          :placeholder="field.placeholder"
          :suffix="SERP_BUDGET_FILTER_TEXTS.currency"
          :size="InputSize.M"
          :name="fieldName(field.bound)"
          :data-testid="fieldName(field.bound)"
          @update:modelValue="onTextInput(field.bound, $event)"
          @change="commitTypedTexts"
          @focus="onFieldFocus(field.bound)"
          @blur="onFieldBlur"
        />
      </div>
    </div>

    <Slider
      class="fr-budget-filter__slider"
      :model-value="sliderValue"
      :min="sliderBounds.min"
      :max="sliderBounds.max"
      :disabled="isSliderDisabled"
      range
      data-testid="budget-filter-slider"
      @update:modelValue="onSliderChange"
    />

    <div
      v-if="group.presets.length"
      class="fr-budget-filter__presets"
    >
      <Chip
        v-for="preset in group.presets"
        :key="preset.value"
        class="fr-budget-filter__preset"
        rounded
        :size="ChipSize.M"
        :active="budget.max === preset.value"
        :disabled="preset.disabled"
        :data-testid="`budget-filter-preset-${preset.value}`"
        @click="onPresetClick(preset)"
      >
        {{ presetLabel(preset.value) }}
      </Chip>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .fr-budget-filter {
    $root: &;
    $presets-per-row: 3;

    display: flex;
    flex-direction: column;

    &__header {
      align-items: baseline;
      display: flex;
      gap: var(--spacing-xs);
      justify-content: space-between;
      margin-bottom: var(--spacing-m);
    }

    &__title {
      color: var(--color-text-primary);
    }

    &__tourists {
      color: var(--color-text-secondary);
      text-align: right;
    }

    &__fields {
      display: flex;
      gap: var(--spacing-xs);
      justify-content: space-between;
    }

    &__field {
      display: flex;
      flex: 0 1 110px;
      flex-direction: column;
      gap: var(--spacing-xs);
      min-width: 0;
    }

    &__field-label {
      color: var(--color-text-secondary);
    }

    &__slider {
      margin-top: var(--spacing-xl);

      &:last-child {
        margin-bottom: var(--spacing-xs);
      }
    }

    &__presets {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-xs);
      margin-top: var(--spacing-m);

      #{$root}__preset {
        cursor: pointer;
        flex: 0 0 calc((100% - var(--spacing-xs) * ($presets-per-row - 1)) / $presets-per-row);
        padding: 0 var(--spacing-xs);
        white-space: nowrap;
      }
    }
  }
</style>
